/**
 * @fileoverview Multi-factor authentication contracts.
 *
 * MFA uses the same discriminated login result as the forced-password change: after a correct
 * password (and after any password-change gate), a client that opted in via
 * `supportsPasswordChallenge` may receive an `mfa_enrollment_required` or `mfa_required` outcome
 * carrying a short-lived `mfaPending` token. The client drives enroll/confirm/verify with that
 * token to complete the deferred login. Legacy clients never see these outcomes.
 */

/**
 * MFA method identifiers.
 *
 * @remarks
 * - `TOTP`: time-based one-time passwords generated by an authenticator application.
 * - `EMAIL`: email-delivered one-time passwords; not currently supported for enrollment.
 */
export enum MfaMethod {
  TOTP = 'totp',
  EMAIL = 'email',
  PASSKEY = 'passkey',
}

/**
 * Payload for starting MFA enrollment.
 *
 * @interface IMfaEnrollStart
 * @property {MfaMethod} [method] - Method to enroll; defaults to `MfaMethod.TOTP`.
 */
export interface IMfaEnrollStart {
  method?: MfaMethod;
}

/**
 * MFA enrollment setup result.
 *
 * @interface IMfaEnrollView
 * @property {MfaMethod} method - The method being enrolled.
 * @property {string} [otpauthUri] - TOTP provisioning URI for an authenticator application.
 * @property {string} [secret] - TOTP shared secret for manual authenticator setup.
 *
 * @remarks
 * TOTP enrollment returns both setup fields. Display them only during enrollment;
 * they contain sensitive credentials and must not be logged.
 */
export interface IMfaEnrollView {
  method: MfaMethod;
  otpauthUri?: string;
  secret?: string;
}

/**
 * Payload for confirming MFA enrollment.
 *
 * @interface IMfaConfirm
 * @property {string} code - TOTP code generated using the newly enrolled secret.
 */
export interface IMfaConfirm {
  code: string;
}

/**
 * Payload for completing an MFA login challenge.
 *
 * @interface IMfaVerify
 * @property {string} code - Current TOTP code or an unused backup code; a successful
 * backup-code verification consumes that code.
 */
export interface IMfaVerify {
  code: string;
}

/**
 * Confirmed MFA enrollment result containing login tokens and backup codes.
 *
 * @interface IMfaConfirmView
 * @property {string} accessToken - Session access token issued after enrollment is confirmed.
 * @property {string} refreshToken - Session refresh token issued after enrollment is confirmed.
 * @property {string[]} backupCodes - Plaintext, single-use recovery codes to store securely.
 *
 * @remarks
 * Backup codes are returned at enrollment confirmation; only their hashes are stored.
 * They cannot be retrieved later and must not be logged.
 */
export interface IMfaConfirmView {
  accessToken: string;
  refreshToken: string;
  backupCodes: string[];
  /**
   * MFA-first: proof that MFA was cleared BEFORE account selection (enrollment confirmed with no
   * session yet). Presented to listAccountsLogin + login. Absent for the account-scoped flow.
   */
  accountsProof?: string;
}

/**
 * Token-authorized gRPC request to start MFA enrollment.
 *
 * @interface IMfaEnrollRequest
 * @property {string} token - Short-lived MFA-pending token authorizing enrollment.
 * @property {MfaMethod} [method] - Method to enroll; defaults to `MfaMethod.TOTP`.
 */
export interface IMfaEnrollRequest {
  token: string;
  method?: MfaMethod;
}

/**
 * Token-authorized gRPC request to confirm enrollment or verify an MFA challenge.
 *
 * @interface IMfaCodeRequest
 * @property {string} token - Short-lived MFA-pending token authorizing the operation.
 * @property {string} code - TOTP code for enrollment confirmation; a TOTP code or unused
 * backup code for login verification.
 * @property {string} ip - IP address of the client completing authentication.
 * @property {string} deviceName - Name of the device completing authentication.
 */
export interface IMfaCodeRequest {
  token: string;
  code: string;
  ip: string;
  deviceName: string;
}

/**
 * Session-authorized gRPC request to disable MFA.
 *
 * @interface IMfaDisableRequest
 * @property {string} token - Session refresh token authorizing the operation, not an MFA-pending token.
 */
export interface IMfaDisableRequest {
  token: string;
}

/**
 * Token-authorized request to START a passkey (WebAuthn) ceremony — registration or authentication.
 * Both only need the mfa-pending token; the server generates a challenge and returns the options.
 *
 * @interface IMfaPasskeyOptionsRequest
 * @property {string} token - Short-lived MFA-pending token authorizing the ceremony.
 */
export interface IMfaPasskeyOptionsRequest {
  token: string;
}

/**
 * WebAuthn options for the browser's `navigator.credentials`, serialized as a JSON string
 * (`PublicKeyCredentialCreationOptionsJSON` for registration, `...RequestOptionsJSON` for
 * authentication). Passed opaquely so the complex WebAuthn shapes need no proto modelling.
 *
 * @interface IMfaPasskeyOptionsView
 * @property {string} optionsJSON - The `@simplewebauthn/browser` options object, JSON-stringified.
 */
export interface IMfaPasskeyOptionsView {
  optionsJSON: string;
}

/**
 * Token-authorized request COMPLETING a passkey ceremony. `response` is the JSON-serialized WebAuthn
 * response the browser produced (an attestation for registration, an assertion for authentication).
 * On success the deferred login completes and session tokens are issued.
 *
 * @interface IMfaPasskeyVerifyRequest
 * @property {string} token - Short-lived MFA-pending token authorizing the ceremony.
 * @property {string} response - The browser's WebAuthn response (`RegistrationResponseJSON` /
 * `AuthenticationResponseJSON`), JSON-stringified.
 * @property {string} ip - IP address of the client completing authentication.
 * @property {string} deviceName - Name of the device completing authentication.
 */
export interface IMfaPasskeyVerifyRequest {
  token: string;
  response: string;
  ip: string;
  deviceName: string;
}

/**
 * Session-authorized request for the MFA management surface (the Security screen). Here `token` is the
 * caller's SESSION token (the endpoint is behind the normal session guard), NOT an mfa-pending token —
 * the user is already signed in and managing their own factors.
 *
 * @interface IMfaSessionRequest
 * @property {string} token - Session token authorizing the management operation.
 */
export interface IMfaSessionRequest {
  token: string;
}

/**
 * A registered passkey as shown on the MFA management screen. Carries no secret material — only what a
 * user needs to recognize and remove the credential.
 *
 * @interface IMfaPasskeyView
 * @property {string} credentialId - The passkey's stable id (used to remove it).
 * @property {string | null} deviceType - `singleDevice` | `multiDevice` when known.
 * @property {boolean} backedUp - Whether the passkey is synced to a cloud keychain.
 * @property {string} createdAt - ISO timestamp the passkey was registered.
 * @property {string | null} lastUsedAt - ISO timestamp of the last successful sign-in, or null if never used.
 * @property {string | null} label - The label the person gave this passkey when adding it, or null when unset.
 */
export interface IMfaPasskeyView {
  credentialId: string;
  deviceType: string | null;
  backedUp: boolean;
  createdAt: string;
  lastUsedAt: string | null;
  label: string | null;
}

/**
 * The account's current MFA factors, for the Security management screen.
 *
 * @interface IMfaStatusView
 * @property {boolean} hasTotp - Whether a confirmed authenticator-app (TOTP) factor exists.
 * @property {boolean} hasEmail - Whether a confirmed email factor exists.
 * @property {boolean} emailAvailable - Whether the email one-time-code fallback is offered; it rides the
 * TOTP code flow, so it mirrors `hasTotp`.
 * @property {IMfaPasskeyView[]} passkeys - The registered passkeys (may be empty).
 */
export interface IMfaStatusView {
  hasTotp: boolean;
  hasEmail: boolean;
  emailAvailable: boolean;
  passkeys: IMfaPasskeyView[];
}

/**
 * Session-authorized request COMPLETING a passkey registration from the management screen. Unlike the
 * login-time ceremony this issues NO session (the user is already signed in) — the credential is just
 * verified, persisted, and returned.
 *
 * @interface IMfaManagePasskeyVerifyRequest
 * @property {string} token - Session token authorizing the operation.
 * @property {string} response - The browser's `RegistrationResponseJSON`, JSON-stringified.
 * @property {string} [label] - Optional human label so a person with several passkeys can tell them apart.
 */
export interface IMfaManagePasskeyVerifyRequest {
  token: string;
  response: string;
  label?: string;
}

/**
 * Session-authorized request to remove one registered passkey. Removal is unconditional (no
 * keep-at-least-one guard): an MFA-required account left with no factor simply re-enrolls at next login.
 *
 * @interface IMfaRemovePasskeyRequest
 * @property {string} token - Session token authorizing the operation.
 * @property {string} credentialId - The passkey to remove; must belong to the caller.
 */
export interface IMfaRemovePasskeyRequest {
  token: string;
  credentialId: string;
}

/**
 * Session-authorized request that performs a STEP-UP before enrolling a new factor from the Security
 * screen: the server re-verifies the account's CURRENT PASSWORD (adding a factor from a live session is
 * a persistence-sensitive change, mirroring `changePassword`). Used to START a factor enrolment —
 * add-passkey options, add-TOTP, and add-email.
 *
 * @interface IMfaStepUpRequest
 * @property {string} token - Session token authorizing the management operation.
 * @property {string} currentPassword - The account's current password, re-verified before enrolling.
 */
export interface IMfaStepUpRequest {
  token: string;
  currentPassword: string;
}

/**
 * Session-authorized request COMPLETING a code-factor enrolment from the Security screen — the emailed
 * or authenticator code. Issues NO session (the user is already signed in). The START step already
 * performed the password step-up, so this does not re-ask for it.
 *
 * @interface IMfaManageCodeRequest
 * @property {string} token - Session token authorizing the operation.
 * @property {string} code - The authenticator (TOTP) code or the emailed one-time code.
 */
export interface IMfaManageCodeRequest {
  token: string;
  code: string;
}

/**
 * TOTP setup material returned when STARTING an authenticator-app enrolment from the Security screen.
 * Display only during setup; never log.
 *
 * @interface IMfaAddTotpStartView
 * @property {string} secret - The TOTP shared secret for manual authenticator setup.
 * @property {string} otpauthUri - The provisioning URI for a QR code.
 */
export interface IMfaAddTotpStartView {
  secret: string;
  otpauthUri: string;
}

/**
 * Result of confirming a Security-screen TOTP enrolment: the one-time backup codes (shown once; only
 * their hashes are stored). No session is issued.
 *
 * @interface IMfaAddTotpVerifyView
 * @property {string[]} backupCodes - Plaintext, single-use recovery codes to store securely.
 */
export interface IMfaAddTotpVerifyView {
  backupCodes: string[];
}
